/**
 * announcerMenu — pure session-scoped gating for the menu announcer lines.
 *
 * The wiring (main.ts) forwards ScreenManager active-screen changes and the
 * title idle timer here; this tracker answers with the line ids that may play
 * (once-per-session lines are consumed on first emit). Rate limiting of rapid
 * back-and-forth navigation is the announcer scheduler's job (menu lines carry
 * a 1.5 s cooldown and never play over match audio) — this class only owns
 * the per-session dedupe.
 */

import type { AnnouncerLineId } from "./announcer.js";

/** ScreenManager screen name → menu line, for screens entered via confirm. */
export const MENU_SCREEN_LINES: Readonly<Record<string, AnnouncerLineId>> = {
  garage: "menu_garage",
  practice: "menu_practice",
  rankings: "menu_rankings",
  options: "menu_options",
  "lobby-landing": "menu_lobbies",
};

export class MenuAnnouncerTracker {
  private titleIntroDone = false;
  private welcomeDone = false;
  private pressStartDone = false;

  /** True once title_intro has been emitted (it may still have been blocked by
   * autoplay policy — the wiring retries on the first title input). */
  get titleIntroEmitted(): boolean {
    return this.titleIntroDone;
  }

  /** A screen became current. Returns the lines to announce (possibly none). */
  screenEntered(name: string | null): AnnouncerLineId[] {
    if (name === "title") {
      if (this.titleIntroDone) return [];
      this.titleIntroDone = true;
      return ["title_intro"];
    }
    if (name === "main-menu") {
      if (this.welcomeDone) return [];
      this.welcomeDone = true;
      return ["welcome"];
    }
    const line = name !== null ? MENU_SCREEN_LINES[name] : undefined;
    return line ? [line] : [];
  }

  /** QUICK MATCH confirmed on the main menu (not a ScreenManager screen). */
  quickmatchChosen(): AnnouncerLineId[] {
    return ["menu_quickmatch"];
  }

  /** ~8 s idle on TITLE — at most once per session. */
  pressStartIdle(): AnnouncerLineId[] {
    if (this.pressStartDone) return [];
    this.pressStartDone = true;
    return ["press_start"];
  }
}

/** App-wide singleton (browser wiring). Tests construct their own. */
export const menuAnnouncer = new MenuAnnouncerTracker();
